import { useEffect, useRef, useState } from 'react';
import type { SortingState } from '@tanstack/react-table';

import { SELECT_COLUMN_ID } from '@/components/data-table/core/utils/table-config-parser';

/** Column ID for the device name column — used as the alphabetical sort when the drawer is open */
export const DEVICE_NAME_COLUMN_ID = 'deviceName';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseBulkEditTableSortOptions {
  /** Whether the edit drawer is currently open */
  isEditDrawerOpen: boolean;
}

interface UseBulkEditTableSortResult {
  /** Handler for DataTable.Sorting onChange */
  onSortingChange: (sorting: SortingState) => void;
  /** Current sorting state for DataTable.Sorting value */
  sorting: SortingState;
}

/**
 * Manages controlled sorting for the bulk-edit table with drawer-aware transitions.
 *
 * Behavior:
 * - **Drawer closed**: sorting is user-controlled via column headers.
 * - **Drawer open**: selected rows are moved to the top while the existing
 *   device name sort direction is preserved.
 * - **Drawer closes**: the previous user sorting is restored.
 */
export const useBulkEditTableSort = ({
  isEditDrawerOpen,
}: UseBulkEditTableSortOptions): UseBulkEditTableSortResult => {
  // Initial sorting state: [name asc]
  const [sorting, setSorting] = useState<SortingState>([
    { id: DEVICE_NAME_COLUMN_ID, desc: false },
  ]);

  // Keep a ref in sync with the latest sorting so the effect can read it
  // without adding `sorting` to the dependency array (which would overwrite
  // the saved sort on every change while the drawer is open).
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;

  // Ref to stash sorting when the drawer opens so we can restore on close.
  const savedSortingRef = useRef<SortingState | null>(null);
  const prevDrawerOpenRef = useRef(isEditDrawerOpen);

  // Handle drawer open/close transitions
  useEffect(() => {
    if (isEditDrawerOpen && !prevDrawerOpenRef.current) {
      // Drawer just opened — save current sorting and prepend selection sort
      savedSortingRef.current = sortingRef.current;

      const existingNameSort = sortingRef.current.find((s) => s.id === DEVICE_NAME_COLUMN_ID);

      setSorting([
        { id: SELECT_COLUMN_ID, desc: false },
        existingNameSort ?? { id: DEVICE_NAME_COLUMN_ID, desc: false },
      ]);
    } else if (!isEditDrawerOpen && prevDrawerOpenRef.current) {
      // Drawer just closed — restore saved sorting
      if (savedSortingRef.current !== null) {
        setSorting(savedSortingRef.current);
        savedSortingRef.current = null;
      }
    }
    prevDrawerOpenRef.current = isEditDrawerOpen;
  }, [isEditDrawerOpen]);

  const handleSortingChange = (nextSorting: SortingState) => {
    // Ignore user-initiated sort changes while the drawer is open;
    // programmatic sorting is applied via the useEffect above.
    if (!isEditDrawerOpen) {
      setSorting(nextSorting);
    }
  };

  return { sorting, onSortingChange: handleSortingChange };
};
